import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../api/client.js";

export interface UseLogArgs {
  readonly slug: string;
  readonly repo: string;
  readonly ref: string;
  /** 1-based page. */
  readonly page?: number;
  readonly pageSize?: number;
}

/** A changed supported-type file in a commit (name-status). */
export interface CommitFileChange {
  readonly status: string;
  readonly path: string;
  readonly oldPath?: string;
}

/** Normalized commit summary the UI renders (author flattened to a name, git
 *  second-precision timestamp converted to ms). `files` is present for external
 *  repos (the name-status timeline is free offline); omitted for internal. */
export interface CommitSummary {
  readonly oid: string;
  readonly parents: readonly string[];
  readonly message: string;
  readonly author: string;
  readonly timestampMs: number;
  readonly files?: readonly CommitFileChange[];
}

export interface LogPayload {
  readonly commits: readonly CommitSummary[];
  readonly total: number | null;
  readonly hasMore: boolean;
}

// The server's wire shape (server/src/git/log.ts ListCommitsResult). External
// repos additionally carry per-commit `files` (name-status, free offline).
interface WireCommit {
  readonly oid: string;
  readonly parents: readonly string[];
  readonly message: string;
  readonly author: { readonly name: string; readonly email: string; readonly timestamp: number };
  readonly files?: readonly CommitFileChange[];
}
interface WireLog {
  readonly commits: readonly WireCommit[];
  readonly total: number | null;
  readonly hasMore: boolean;
}

export function useLog(args: UseLogArgs) {
  const { slug, repo, ref, page = 1, pageSize = 50 } = args;
  return useQuery({
    queryKey: ["log", slug, repo, ref, page, pageSize],
    queryFn: async ({ signal }): Promise<LogPayload> => {
      // The server route reads ?page&pageSize (1-based) and returns author as an
      // object with a second-precision timestamp — normalize both here so the
      // components get a flat, render-ready shape (bugloop L3).
      const url =
        `/api/ws/${encodeURIComponent(slug)}/r/${encodeURIComponent(repo)}/log` +
        `?ref=${encodeURIComponent(ref)}&page=${page}&pageSize=${pageSize}`;
      const raw = await apiFetch<WireLog>(url, { signal });
      return {
        total: raw.total,
        hasMore: raw.hasMore,
        commits: raw.commits.map((c) => ({
          oid: c.oid,
          parents: c.parents,
          message: c.message,
          author: c.author.name,
          timestampMs: c.author.timestamp * 1000,
          files: c.files,
        })),
      };
    },
    enabled: Boolean(slug && repo && ref),
    staleTime: 60 * 1000,
  });
}
